import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { MobileDrawer } from 'wix-ui-tpa/cssVars';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { CloseSmall } from '@wix/wix-ui-icons-common/on-stage';
import { getInternalLinkRoute } from '@wix/communities-blog-client-common';
import { useKeyPress } from '../../hooks/use-key-press';
import { getActiveLink, getFeedAndCategoryLinks } from '../../selectors/category-links-selectors';
import { isSeo as getIsSeo } from '../../store/basic-params/basic-params-selectors';
import { getTopology } from '../../store/topology/topology-selectors';
import { ArrowDownIcon } from '../icons/arrow-down-icon';
import { useSelector } from '../runtime-context';
import SeoSelect from './seo-select';
import styles from './category-drawer.scss';

const CategoryDrawer: React.FC = () => {
  const links = useSelector(getFeedAndCategoryLinks);
  const activeCategoryLink = useSelector(getActiveLink);
  const isSeo = useSelector(getIsSeo);
  const topology = useSelector(getTopology);
  const listRef = useRef<HTMLUListElement>(null);
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  const handleToggleClick = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  useKeyPress('Escape', handleClose);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (listRef.current && isOpen) {
        const activeLink =
          listRef.current.querySelector<HTMLAnchorElement>('[aria-current="page"]');
        activeLink?.scrollIntoView({
          block: 'center',
          inline: 'nearest',
        });
        activeLink?.focus();
      }
    });
  }, [isOpen]);

  if (isSeo) {
    return <SeoSelect />;
  }

  return (
    <div className={styles.root}>
      <button
        data-hook="category-dropdown"
        type="button"
        onClick={handleToggleClick}
        aria-label={t('category-change.select-name')}
        className={classNames(
          styles.trigger,
          'blog-card-background-color',
          'blog-text-color',
          'blog-navigation-container-font',
        )}
      >
        <span data-hook="category-title" className={styles.triggerTitle}>
          {activeCategoryLink.text}
        </span>
        <ArrowDownIcon className={classNames('blog-icon-fill', styles.triggerIcon)} />
      </button>
      <MobileDrawer isOpen={isOpen} onRequestClose={handleClose}>
        <nav className={styles.nav}>
          <ul ref={listRef}>
            {links.map((link) => {
              const isActive = link.path === activeCategoryLink.path;
              const href = getInternalLinkRoute({
                topology,
                to: link.path,
                shouldNavigateToFeed: true,
              });

              return (
                <li key={link.key}>
                  <a
                    href={href}
                    aria-current={isActive ? 'page' : undefined}
                    className={styles.link}
                    onClick={handleClose}
                  >
                    {link.text}
                  </a>
                </li>
              );
            })}
            <li className={styles.itemSticky}>
              <button type="button" onClick={handleClose} className={styles.link}>
                <CloseSmall /> Close
              </button>
            </li>
          </ul>
        </nav>
      </MobileDrawer>
    </div>
  );
};

export default CategoryDrawer;
